"use client";

import { useState } from "react";
import { clsx } from "clsx";
import type { SideBySideImageRevealBlock } from "@mw/shared/payload-types";
import { getImageProps } from "@utilities/payload";
import NextImage, { type ImageProps } from "next/image";
import { sizes } from "@ui/lib/sizes";
import { RevealImageContainer } from "@ui/components/core/RevealImageContainer";

export function BlockSideBySideImageReveal({ images }: SideBySideImageRevealBlock) {
  const [activeIndex, setActiveIndex] = useState(1);

  const validImages =
    images
      ?.map(image => ({ ...image, imageProps: getImageProps(image.image) }))
      .filter(image => image.title && image.description && image.imageProps) ?? [];

  if (validImages.length !== 2) {
    return null;
  }

  const left = validImages[0];
  const right = validImages[1];

  return (
    <div className='tw-flow-root'>
      <div
        className='tw-pt-1 -tw-mt-1 md:tw-grid tw-isolate md:tw-gap-y-11 md:tw-rounded-b-3xl md:tw-overflow-hidden'
        style={
          {
            "--breakout-padding": "max(0.5rem, calc(var(--full-bleed) - 1rem))",
            "--breakout-margin": "calc(var(--breakout-padding) * -1)",
            gridTemplateColumns: "40% 20% 40%",
            marginLeft: "var(--breakout-margin)",
            marginRight: "var(--breakout-margin)",
            paddingLeft: "var(--breakout-padding)",
            paddingRight: "var(--breakout-padding)",
          } as React.CSSProperties
        }
      >
        {/* Desktop background */}
        <div
          className='tw-row-start-2 tw-col-start-1 tw-col-span-full tw-bg-cream tw-rounded-3xl'
          style={{ marginLeft: "var(--breakout-margin)", marginRight: "var(--breakout-margin)" }}
        />

        {/* Desktop hairline border */}
        <div
          className='tw-row-start-2 tw-col-start-1 tw-col-span-full tw-pointer-events-none tw-rounded-3xl tw-ring-0.5 tw-ring-black/7.5 tw-ring-inset tw-relative tw-z-50 tw-hidden md:tw-block'
          style={{ marginLeft: "var(--breakout-margin)", marginRight: "var(--breakout-margin)" }}
        />

        <TextContent title={left.title} description={left.description} />
        <Image
          props={left.imageProps}
          position='left'
          isActive={activeIndex === 0}
          onHoverStart={() => setActiveIndex(0)}
          className='tw-col-start-1'
        />

        <TextContent
          title={right.title}
          description={right.description}
          className='tw-col-span-2 tw-mt-12 md:tw-mt-0'
        />
        <Image
          props={right.imageProps}
          position='right'
          isActive={activeIndex === 1}
          onHoverStart={() => setActiveIndex(1)}
          className='tw-col-start-2'
        />
      </div>
    </div>
  );
}

function TextContent({ title, description, className }: { title: string; description: string; className?: string }) {
  return (
    <div className={clsx("tw-row-start-1", className)}>
      <div className='tw-max-w-64 md:tw-max-w-60 lg:tw-max-w-64'>
        <h3 className='tw-type-base tw-font-medium tw-text-black'>{title}</h3>
        <p className='tw-mt-4 tw-type-sm tw-text-gray-500 tw-text-pretty'>{description}</p>
      </div>
    </div>
  );
}

function Image({
  props,
  position,
  isActive,
  onHoverStart,
  className,
}: {
  props: ImageProps | null;
  position: "left" | "right";
  isActive: boolean;
  onHoverStart: () => void;
  className?: string;
}) {
  if (!props) {
    return null;
  }

  return (
    <div
      className={clsx(
        "tw-relative tw-mt-8 md:tw-mt-0 tw-bg-cream md:tw-bg-transparent tw-col-span-2 tw-overflow-hidden tw-row-start-2 tw-pt-6 md:tw-pt-9 tw-px-5 md:tw-px-0 tw-rounded-2xl md:tw-rounded-none",
        className,
      )}
    >
      <RevealImageContainer
        position={position}
        isActive={isActive}
        onHoverStart={onHoverStart}
        className='max-md:!tw-translate-y-0 max-md:![mask-image:none]'
      >
        <NextImage {...props} className='tw-w-full' sizes={sizes("100vw", { md: "min(50vw, 40.5rem)" })} />
      </RevealImageContainer>

      {/* Mobile hairline border */}
      <div className='tw-absolute tw-z-50 tw-inset-0 tw-pointer-events-none tw-rounded-2xl tw-ring-0.5 tw-ring-black/7.5 tw-ring-inset md:tw-hidden' />
    </div>
  );
}
